import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, Send } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    service: '',
    date: '',
    time: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit consultation booking');
      }

      const result = await response.json();
      console.log('Consultation booked:', result);

      setIsSubmitted(true);
      toast.success('Consultation booked successfully! We\'ll contact you shortly.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          service: '',
          date: '',
          time: '',
          message: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast.error('Failed to book consultation. Please try again or call us directly.');
    }
  };

  if (isSubmitted) {
    return (
      <section id="consultation" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-2xl bg-white">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8BC34A] to-[#7CB342] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Consultation Booked Successfully!</h3>
                <p className="text-lg text-gray-600 mb-6">
                  Thank you for choosing Visaspot. Our expert counselor will contact you within 24 hours to confirm your consultation.
                </p>
                <p className="text-sm text-gray-500">
                  Check your email for confirmation details.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="consultation" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#FF7043] to-[#F4511E] text-white px-4 py-2 rounded-full text-sm font-semibold">
              Book Your Consultation
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Expert Guidance for Free
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Schedule a free consultation with our certified counselors and take the first step towards your dream destination.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-2xl bg-white">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <User size={16} className="text-[#1565C0]" />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <Mail size={16} className="text-[#1565C0]" />
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <Phone size={16} className="text-[#1565C0]" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (234) 567-890"
                      className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                      required
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <Label htmlFor="country" className="text-gray-700 font-semibold mb-2">
                      Preferred Country
                    </Label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1565C0] focus:border-transparent"
                    >
                      <option value="">Select a country</option>
                      <option value="canada">Canada</option>
                      <option value="usa">USA</option>
                      <option value="uk">United Kingdom</option>
                      <option value="australia">Australia</option>
                      <option value="europe">Europe</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Service */}
                  <div>
                    <Label htmlFor="service" className="text-gray-700 font-semibold mb-2">
                      Service Interested In
                    </Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1565C0] focus:border-transparent"
                    >
                      <option value="">Select a service</option>
                      <option value="study-abroad">Study Abroad Consulting</option>
                      <option value="immigration">Immigration Services</option>
                      <option value="student-visa">Student Visa Assistance</option>
                      <option value="work-permit">Work Permit & PR</option>
                    </select>
                  </div>

                  {/* Preferred Date */}
                  <div>
                    <Label htmlFor="date" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <Calendar size={16} className="text-[#1565C0]" />
                      Preferred Date
                    </Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                    />
                  </div>

                  {/* Preferred Time */}
                  <div>
                    <Label htmlFor="time" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <Clock size={16} className="text-[#1565C0]" />
                      Preferred Time
                    </Label>
                    <select
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1565C0] focus:border-transparent"
                    >
                      <option value="">Select time slot</option>
                      <option value="morning">Morning (9 AM - 12 PM)</option>
                      <option value="afternoon">Afternoon (12 PM - 3 PM)</option>
                      <option value="evening">Evening (3 PM - 6 PM)</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="md:col-span-2">
                    <Label htmlFor="message" className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                      <MessageSquare size={16} className="text-[#1565C0]" />
                      Additional Information
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your educational background, goals, or any specific questions..."
                      rows={4}
                      className="mt-1 border-gray-300 focus:border-[#1565C0] focus:ring-[#1565C0]"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-[#FF7043] to-[#F4511E] hover:from-[#F4511E] hover:to-[#FF7043] text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Book Free Consultation
                  <Send className="ml-2" size={20} />
                </Button>

                <p className="text-sm text-gray-500 text-center mt-4">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ConsultationForm;
